import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { streamResearch } from "@/lib/research";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function reduce(snap, event, data) {
  if (event === "sync") return { ...data };
  if (!snap) return snap;
  if (event === "stage") return { ...snap, stage: data.stage };
  if (event === "plan") return { ...snap, plan: data.queries };
  if (event === "sources") return { ...snap, sources: data.items };
  if (event === "search_progress") return { ...snap, searches: data };
  if (event === "read_progress") return { ...snap, reads: data };
  if (event === "delta") return { ...snap, text: snap.text + data.text };
  if (event === "error") return { ...snap, error: data };
  if (event === "done") return { ...snap, status: data.status, stage: "done", done: data };
  return snap;
}

// Live view of a running research job. Reconnects if the stream drops; refreshes cached data when it finishes.
export function useResearchStream(sessionId, enabled) {
  const qc = useQueryClient();
  const [state, setState] = useState(null);
  const snapRef = useRef(null);
  useEffect(() => {
    if (!sessionId || !enabled) return undefined;
    const ctrl = new AbortController();
    let frame = null;
    let finished = false;
    const flush = () => {
      frame = null;
      setState(snapRef.current);
    };
    const onEvent = (event, data) => {
      snapRef.current = reduce(snapRef.current, event, data);
      if (event === "done") finished = true;
      if (event === "delta") frame = frame || requestAnimationFrame(flush);
      else flush();
    };
    (async () => {
      for (let attempt = 0; attempt < 4 && !finished && !ctrl.signal.aborted; attempt += 1) {
        try {
          await streamResearch(sessionId, onEvent, ctrl.signal);
        } catch (e) {
          if (e?.name === "AbortError") return;
          if (e?.status === 404) break;
        }
        if (!finished) await sleep(1500 * (attempt + 1));
      }
      if (ctrl.signal.aborted) return;
      ["research-session", "research-sessions", "me", "usage", "conversation", "files", "project"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    })();
    return () => {
      ctrl.abort();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sessionId, enabled, qc]);
  return state;
}
